import { useState } from "react";
import { TrendUp, Warning, CheckCircle, Factory, ChartBar, Timer, Info } from "@phosphor-icons/react";
import { fmt, fmtInt, fmtPct, brDate } from "./util";

function Dica({ texto, testid }) {
  const [on, setOn] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: 6 }} onMouseEnter={() => setOn(true)} onMouseLeave={() => setOn(false)}>
      <button type="button" onClick={() => setOn(!on)} aria-label="base de cálculo" data-testid={testid} style={{ lineHeight: 0, color: "#A1A1AA" }}><Info size={14} weight="bold" /></button>
      {on && <span className="dash-tooltip" role="tooltip">{texto}</span>}
    </span>
  );
}

function Card({ label, sub, value, icon: Icon, accent, dica, testid, children, alerta }) {
  return (
    <div className="pcp-card dash-kpi" data-testid={testid} data-alerta={alerta ? "true" : undefined}>
      {accent && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="kpi-label" style={{ display: "flex", alignItems: "center" }}>{label}{dica && <Dica texto={dica} testid={`${testid}-dica`} />}</div>
          {value !== undefined && <div className="kpi-value" style={{ marginTop: 6, color: alerta ? "#DC2626" : undefined }} data-testid={`${testid}-valor`}>{value}</div>}
          {children}
          {sub && <div className="dash-kpi-sub" data-testid={`${testid}-sub`}>{sub}</div>}
        </div>
        {Icon && <Icon size={24} weight="bold" style={{ color: accent || "#0A0A0A", flexShrink: 0 }} />}
      </div>
    </div>
  );
}

export default function ResumoExecutivo({ resumo, filtros, regras }) {
  const r = resumo;
  const periodo = `${brDate(filtros.inicio)} – ${brDate(filtros.fim)}`;
  const posicao = `posição em ${brDate(filtros.referencia)}`;
  const aps = r.os_atrasadas_por_status || {};
  const otif = r.otif || {};
  return (
    <div className="dash-kpi-grid" data-testid="resumo-executivo">
      <Card label="OS em execução" value={fmtInt(r.os_em_execucao)} icon={TrendUp} accent="#FFCC00" testid="kpi-execucao"
        sub={`${posicao} · status "Em Execução"`} dica={regras.os_em_execucao} />
      <Card label="OS atrasadas" value={fmtInt(r.os_atrasadas)} icon={Warning} accent="#FF3B30" testid="kpi-atrasadas" alerta={r.os_atrasadas > 0}
        sub={r.os_atrasadas > 0
          ? `${posicao} · ${["Em Execução", "Pendente", "Paralisada"].filter((s) => aps[s]).map((s) => `${aps[s]} ${s.toLowerCase()}`).join(" · ")}`
          : `${posicao} · prazo vencido, OS ativas`}
        dica={regras.os_atrasadas} />
      <Card label="OS concluídas no período" value={fmtInt(r.os_concluidas_periodo)} icon={CheckCircle} accent="#34C759" testid="kpi-concluidas"
        sub={`${periodo} · pela data de conclusão${otif.sem_data_conclusao ? ` · ${otif.sem_data_conclusao} concluída(s) sem data não entram` : ""}`}
        dica={regras.os_concluidas_periodo} />
      <Card label="Produção do período" icon={Factory} testid="kpi-producao" sub={`${periodo} · por atividade e unidade`} dica={regras.producao_periodo}>
        {r.producao_periodo.length === 0 ? (
          <div className="dash-vazio" data-testid="kpi-producao-vazio">Sem produção registrada no período</div>
        ) : (
          <div className="dash-kpi-lista">
            {r.producao_periodo.map((p) => (
              <div key={p.atividade} className="dash-kpi-linha" data-testid={`kpi-producao-${p.categoria}-${p.unidade}`}>
                <span className="font-mono font-semibold" style={{ fontSize: "1.15rem" }}>{fmt(p.quantidade)} <span translate="no" style={{ fontSize: "0.8rem", color: "#71717A" }}>{p.unidade}</span></span>
                <span style={{ fontSize: "0.78rem", color: "#52525B" }}>{p.atividade.replace(/ \(.*\)$/, "")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card label="Avanço físico" icon={ChartBar} testid="kpi-avanco" sub="executado acumulado ÷ planejado total das OS do recorte, mesma unidade" dica={regras.avanco_fisico}>
        {r.avanco_fisico.length === 0 ? (
          <div className="dash-vazio" data-testid="kpi-avanco-vazio">Sem itens planejados no recorte</div>
        ) : (
          <div className="dash-kpi-lista">
            {r.avanco_fisico.map((a) => (
              <div key={a.atividade} className="dash-kpi-linha" data-testid={`kpi-avanco-${a.categoria}-${a.unidade}`}>
                <span className="font-mono font-semibold" style={{ fontSize: "1.15rem", color: a.pct > 100 ? "#DC2626" : undefined }}>{fmtPct(a.pct)}</span>
                <span style={{ fontSize: "0.78rem", color: "#52525B" }}>{a.atividade}</span>
                <div className="progress-track" style={{ height: 4, gridColumn: "1 / -1" }}><div className="progress-fill" style={{ width: `${Math.min(100, a.pct || 0)}%` }} /></div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card label="OTIF" icon={Timer} testid="kpi-otif" dica={regras.otif}
        sub={`${fmtInt(otif.concluidas_total)} concluída(s) no total · ${fmtInt(otif.sem_data_conclusao)} sem data de conclusão · ${fmtInt(otif.sem_prazo)} sem prazo`}>
        <div style={{ marginTop: 6 }}>
          <span className="dash-badge-validacao" data-testid="kpi-otif-valor">Em validação</span>
          <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 6 }}>
            Regra atual daria {otif.valor_regra_atual == null ? "—" : fmtPct(otif.valor_regra_atual)}, mas a conclusão automática não grava data e falta o critério de entrega completa.
          </div>
        </div>
      </Card>
    </div>
  );
}
